import { DateOnly } from '@if/core/date'
import { useSyncExternalStore } from 'react'

/**
 * Single source of "now" for the app. A tester can pin the calendar day (outside prod); the
 * time of day keeps following the real Eastern wall clock so the noon-ET rules behave as they
 * would on that day. Persisted per tab in sessionStorage as `YYYY-MM-DD`.
 */

const STORAGE_KEY = 'simulated-date'
const EASTERN_TIME_ZONE = 'America/Toronto'

const listeners = new Set<() => void>()

const readStorage = (): string | null => {
  try {
    return globalThis.sessionStorage?.getItem(STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

const writeStorage = (value: string | null): void => {
  try {
    if (value === null) globalThis.sessionStorage?.removeItem(STORAGE_KEY)
    else globalThis.sessionStorage?.setItem(STORAGE_KEY, value)
  } catch {
    // sessionStorage unavailable (privacy mode, SSR): the override is simply not kept.
  }
}

const easternParts = (
  date: Date,
): { year: number; month: number; day: number; hour: number; minute: number; second: number } => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: EASTERN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const lookup = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0')
  return {
    year: lookup('year'),
    month: lookup('month'),
    day: lookup('day'),
    hour: lookup('hour'),
    minute: lookup('minute'),
    second: lookup('second'),
  }
}

/** Instant whose Eastern wall clock reads the given components. */
const fromEasternParts = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): Date => {
  // Treat the wall clock as UTC, then shift by the Eastern offset in effect at that instant.
  // Two passes settle the offset across a DST boundary.
  let guess = Date.UTC(year, month - 1, day, hour, minute, second)
  for (let i = 0; i < 2; i++) {
    const p = easternParts(new Date(guess))
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
    guess += Date.UTC(year, month - 1, day, hour, minute, second) - asUtc
  }
  return new Date(guess)
}

export const getSimulatedDate = (): DateOnly | null => {
  const stored = readStorage()
  return stored ? DateOnly.fromString(stored) : null
}

export const setSimulatedDate = (date: DateOnly | null): void => {
  writeStorage(date ? date.toString() : null)
  listeners.forEach((listener) => listener())
}

/** Real instant, or the current Eastern time of day placed on the simulated date. */
export const getTimeNow = (): Date => {
  const simulated = getSimulatedDate()
  const now = new Date()
  if (!simulated) return now
  const { hour, minute, second } = easternParts(now)
  return fromEasternParts(simulated.getFullYear(), simulated.getMonth() + 1, simulated.getDate(), hour, minute, second)
}

export const getToday = (): DateOnly => getSimulatedDate() ?? DateOnly.today()

export const subscribeSimulatedDate = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const useSimulatedDate = (): DateOnly | null => {
  const stored = useSyncExternalStore(subscribeSimulatedDate, readStorage, readStorage)
  return stored ? DateOnly.fromString(stored) : null
}
